<template>
  <v-dialog
    v-model="dialog"
    :max-width="isMobile ? '95vw' : '800px'"
    persistent
    scrollable
    hide-overlay
    transition="dialog-bottom-transition"
    content-class="rounded-lg preview-modal"
    elevation="24"
    rounded
  >
    <v-card>
      <!-- Header -->
      <v-card-title class="d-flex align-center flex-wrap preview-title">
        <span class="text-truncate flex-grow-1 mr-2" :class="isMobile ? 'mobile-title' : ''" :title="title">{{ title }}</span>
        <div class="d-flex align-center">
          <v-btn
            v-if="contentType === CONTENT_TYPES.IMAGE || contentType === CONTENT_TYPES.PDF"
            icon
            @click="downloadFile"
            class="ml-0 mt-2 mt-sm-0 download-btn"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Metadata for JSON/CSV -->
        <div v-if="showMetadata">
          <div class="mb-2">
            <strong>{{ $vuetify.lang.t("$vuetify.insights.section_id") }}:</strong> {{ referenceData.section_id }}
          </div>
          <div class="mb-4">
            <strong>{{ $vuetify.lang.t("$vuetify.insights.dataset_id") }}:</strong> {{ referenceData.dataset_id }}
          </div>
        </div>

        <!-- Content Area -->
        <div :style="contentAreaStyle">
          <!-- Image Display -->
          <div v-if="contentType === CONTENT_TYPES.IMAGE" class="grey lighten-4 d-flex align-center justify-center" style="min-height: 400px;">
            <img 
              :src="imageUrl"
              :alt="title"
              :key="imageUrl"
              style="max-width: 100%; height: auto; display: block;"
            />
          </div>

          <!-- PDF Display -->
          <div v-else-if="contentType === CONTENT_TYPES.PDF" style="min-height: 500px;">
            <iframe
              :src="imageUrl"
              style="width: 100%; height: 500px; border: none;"
              :title="title"
            ></iframe>
          </div>

          <!-- CSV/JSON Display using existing pattern -->
          <v-container v-else class="pa-0">
            <v-row class="pa-0 ma-0" :class="isMobile ? 'd-none' : ''">
              <v-col class="py-1 my-0" cols="6">
                <strong>{{ $vuetify.lang.t("$vuetify.insights.key") }}</strong>
              </v-col>
              <v-col class="py-1 my-0" cols="6">
                <strong>{{ $vuetify.lang.t("$vuetify.insights.value") }}</strong>
              </v-col>
            </v-row>
            <v-row
              class="pa-0 ma-0 kv-row"
              v-for="(value, key) in parsedContent"
              :key="key"
            >
              <v-col class="py-1 my-0 key-col" :cols="isMobile ? 12 : 6">{{ key }}</v-col>
              <v-col class="py-1 my-0 value-col" :cols="isMobile ? 12 : 6">
                <span v-if="value && value.trim()">{{ value }}</span>
                <span v-else class="grey--text">-</span>
              </v-col>
            </v-row>
            <v-row v-if="Object.keys(parsedContent).length === 0" class="pa-0 ma-0">
              <v-col cols="12" class="text-center grey--text pa-4">
                {{ $vuetify.lang.t("$vuetify.common.no_data_available") }}
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="d-flex justify-end preview-actions">
        <v-btn outlined color="error" @click="dialog = false" class="close-btn">
          {{ $vuetify.lang.t("$vuetify.common.close_btn") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { CONTENT_TYPES } from "@/global/_constants";

export default {
  name: "PreviewModal",
  props: {
    value: Boolean,
    title: String,
    referenceData: {
      type: Object,
      default() {
        return {
          section_id: '',
          dataset_id: '',
          reference_url: '',
          content: '',
          original_file_name: ''
        };
      },
    },
    contentType: {
      type: String,
      default: CONTENT_TYPES.TEXT,
    },
  },
  data() {
    return {
      CONTENT_TYPES
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify?.breakpoint?.smAndDown;
    },
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    showMetadata() {
      return (
        (this.contentType === CONTENT_TYPES.JSON || this.contentType === CONTENT_TYPES.CSV) &&
        this.referenceData?.section_id
      );
    },
    imageUrl() {
      return this.referenceData?.reference_url || '';
    },
    contentAreaStyle() {
      // Only add scroll for images and PDFs, not for CSV/JSON
      if (this.contentType === CONTENT_TYPES.IMAGE || this.contentType === CONTENT_TYPES.PDF) {
        return 'max-height: 600px; overflow-y: auto;';
      }
      return '';
    },
    parsedContent() {
      if (!this.referenceData?.content) {
        return {};
      }

      const content = this.referenceData.content;
      
      if (this.contentType === CONTENT_TYPES.JSON) {
        try {
          const parsed = JSON.parse(content);
          if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return this.cleanParsedObject(parsed);
          }
        } catch (e) {
          // If JSON parsing fails, fall through to colon-based parsing
        }
      }


      if (this.contentType === CONTENT_TYPES.CSV) {
        try {
          return this.parseCSV(content);
        } catch (e) {
          // If CSV parsing fails, fall through to colon-based parsing
        }
      }

      // if json/csv failed
      return this.parseColonBased(content);
    },
  },
  methods: {
    downloadFile() {
      const url = this.imageUrl;
      if (!url) return;
      
      // Ensure URL is properly encoded for spaces
      const encodedUrl = url.replace(/ /g, '%20');
      
      // Determine file extension based on content type
      const extension = this.contentType === CONTENT_TYPES.PDF ? 'pdf' : 'jpg';
      const filename = (this.title || 'download').replace(/[^a-z0-9]/gi, '_').toLowerCase() + '.' + extension;
      
      const a = document.createElement('a');
      a.href = encodedUrl;
      a.download = filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    cleanParsedObject(obj) {
      const result = {};
      const cleanValue = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') {
          return JSON.stringify(value);
        }
        return String(value)
          .replace(/<br[^>]*data-cke-filler[^>]*>/gi, '')
          .replace(/<[^>]+>/g, '')
          .trim();
      };

      for (const [key, value] of Object.entries(obj)) {
        if (key !== 'Section ID' && key !== 'Dataset ID' && key !== 'idx' && key !== 'section_id' && key !== 'dataset_id') {
          result[key] = cleanValue(value);
        }
      }
      return result;
    },
    parseCSV(content) {
      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length === 0) return {};

      const firstLine = lines[0];
      const hasHeader = firstLine.includes(',');
      
      if (!hasHeader) {
        return this.parseColonBased(content);
      }

      const result = {};
      const headers = firstLine.split(',').map(h => h.trim());
      
      if (lines.length > 1) {
        const dataLine = lines[1];
        const values = dataLine.split(',').map(v => v.trim());
        
        headers.forEach((header, index) => {
          if (header && header !== 'Section ID' && header !== 'Dataset ID' && header !== 'idx') {
            const value = values[index] || '';
            result[header] = this.cleanValue(value);
          }
        });
      } else {
        return {};
      }

      return result;
    },
    parseColonBased(content) {
      const lines = content.split('\n').filter(line => line.trim());
      const result = {};

      const cleanValue = (value) => {
        if (!value) return '';
        return value
          .replace(/<br[^>]*data-cke-filler[^>]*>/gi, '')
          .replace(/<[^>]+>/g, '')
          .trim();
      };

      lines.forEach(line => {
        const colonIndex = line.indexOf(':');
        if (colonIndex > 0) {
          const key = line.substring(0, colonIndex).trim();
          const value = cleanValue(line.substring(colonIndex + 1));
          if (key !== 'Section ID' && key !== 'Dataset ID' && key !== 'idx') {
            result[key] = value;
          }
        }
      });

      return result;
    },
    cleanValue(value) {
      if (!value) return '';
      return String(value)
        .replace(/<br[^>]*data-cke-filler[^>]*>/gi, '')
        .replace(/<[^>]+>/g, '')
        .trim();
    },
  },
};
</script>

<style scoped>
.kv-row + .kv-row {
  border-top: 1px solid #f0f0f0;
}

.key-col {
  font-weight: 600;
}

.value-col {
  word-break: break-word;
}

.preview-modal {
  padding-bottom: 16px;
}

.preview-title {
  row-gap: 8px;
  min-height: 64px;
}

.preview-actions {
  padding-bottom: 16px;
  padding-right: 16px;
}

.mobile-title {
  white-space: normal !important;
  word-break: break-word;
  max-width: calc(100% - 60px);
  line-height: 1.3;
}

.download-btn {
  flex-shrink: 0;
}

.close-btn {
  z-index: 10;
  position: relative;
}

@media (max-width: 600px) {
  .preview-modal {
    padding-bottom: 80px; /* Extra room for floating chat widgets */
  }
  
  .preview-actions {
    padding-bottom: 32px;
    padding-right: 16px;
    margin-right: 0;
  }
  
  .preview-title {
    min-height: auto;
    padding-bottom: 8px;
  }
  
  .mobile-title {
    font-size: 0.95rem;
    max-width: calc(100% - 50px);
  }
  
  .download-btn {
    margin-left: 8px;
    margin-top: 0;
  }
}
</style>
